import {
  Document, Packer, Paragraph, TextRun, Table, TableRow, TableCell,
  HeadingLevel, AlignmentType, BorderStyle, WidthType, ShadingType,
  LevelFormat
} from 'docx'
import { saveAs } from 'file-saver'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

// ============================================================
// Markdown → docx parsing
// ============================================================

function parseInlineFormatting(text) {
  const runs = []
  // Match **bold**, *italic*, and [PLACEHOLDER] brackets
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|\[([^\]]+)\])/g
  let lastIndex = 0
  let match

  while ((match = regex.exec(text)) !== null) {
    // Add any text before this match
    if (match.index > lastIndex) {
      runs.push(new TextRun({ text: text.slice(lastIndex, match.index) }))
    }

    if (match[2]) {
      // **bold**
      runs.push(new TextRun({ text: match[2], bold: true }))
    } else if (match[3]) {
      // *italic*
      runs.push(new TextRun({ text: match[3], italics: true }))
    } else if (match[4]) {
      // [PLACEHOLDER] — highlight yellow
      runs.push(new TextRun({
        text: `[${match[4]}]`,
        highlight: 'yellow'
      }))
    }

    lastIndex = match.index + match[0].length
  }

  // Add remaining text
  if (lastIndex < text.length) {
    runs.push(new TextRun({ text: text.slice(lastIndex) }))
  }

  // If no runs were created, add the full text as-is
  if (runs.length === 0) {
    runs.push(new TextRun({ text }))
  }

  return runs
}

function buildTable(rows) {
  const parsed = rows.map(row =>
    row.split('|').slice(1, -1).map(cell => cell.trim())
  )

  if (parsed.length === 0) return null

  const colCount = parsed[0].length
  const colWidth = Math.floor(9360 / colCount)

  const tableRows = parsed.map((cells, rowIndex) =>
    new TableRow({
      children: cells.map(cell =>
        new TableCell({
          width: { size: colWidth, type: WidthType.DXA },
          shading: rowIndex === 0
            ? { fill: 'E8EDF2', type: ShadingType.CLEAR, color: 'auto' }
            : undefined,
          children: [
            new Paragraph({
              children: rowIndex === 0
                ? [new TextRun({ text: cell, bold: true, size: 18, font: 'Calibri' })]
                : parseInlineFormatting(cell)
            })
          ]
        })
      )
    })
  )

  return new Table({
    rows: tableRows,
    width: { size: 9360, type: WidthType.DXA },
    borders: {
      top: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
      bottom: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
      left: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
      right: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
      insideVertical: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' },
    }
  })
}

function parseMarkdownToDocx(content) {
  const children = []
  const lines = content.split('\n')
  const tableRows = []

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    // Flush accumulated table rows if the current line is NOT a table row
    if (!line.startsWith('|') && tableRows.length > 0) {
      const table = buildTable(tableRows)
      if (table) children.push(table)
      tableRows.length = 0
    }

    // Headings
    if (line.startsWith('### ')) {
      children.push(new Paragraph({
        heading: HeadingLevel.HEADING_3,
        children: parseInlineFormatting(line.slice(4))
      }))
    } else if (line.startsWith('## ')) {
      children.push(new Paragraph({
        heading: HeadingLevel.HEADING_2,
        children: parseInlineFormatting(line.slice(3))
      }))
    } else if (line.startsWith('# ')) {
      children.push(new Paragraph({
        heading: HeadingLevel.HEADING_1,
        children: parseInlineFormatting(line.slice(2))
      }))
    }
    // Bullet lists
    else if (line.match(/^[\s]*[-*]\s/)) {
      children.push(new Paragraph({
        numbering: { reference: 'bullets', level: 0 },
        children: parseInlineFormatting(line.replace(/^[\s]*[-*]\s/, ''))
      }))
    }
    // Numbered lists
    else if (line.match(/^[\s]*\d+\.\s/)) {
      children.push(new Paragraph({
        numbering: { reference: 'numbers', level: 0 },
        children: parseInlineFormatting(line.replace(/^[\s]*\d+\.\s/, ''))
      }))
    }
    // Table rows
    else if (line.startsWith('|')) {
      // Skip separator rows (|---|---|)
      if (!line.match(/^\|[\s\-:|]+\|$/)) {
        tableRows.push(line)
      }
    }
    // Horizontal rule
    else if (line.match(/^[-*_]{3,}$/)) {
      children.push(new Paragraph({
        spacing: { before: 200, after: 200 },
        border: { bottom: { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' } }
      }))
    }
    // Empty lines — skip
    else if (line.trim() === '') {
      // spacing handled by paragraph spacing
    }
    // Regular paragraphs
    else {
      children.push(new Paragraph({
        spacing: { after: 200 },
        children: parseInlineFormatting(line)
      }))
    }
  }

  // Flush any remaining table rows
  if (tableRows.length > 0) {
    const table = buildTable(tableRows)
    if (table) children.push(table)
  }

  return children
}

// ============================================================
// DOCX Export
// ============================================================

export async function exportPolicyToDocx({ title, domain, content, frameworkTags = [] }) {
  const contentParagraphs = parseMarkdownToDocx(content || '')

  const headerChildren = [
    new Paragraph({
      heading: HeadingLevel.HEADING_1,
      children: [new TextRun({ text: title, bold: true })]
    }),
    new Paragraph({
      spacing: { after: 300 },
      children: [new TextRun({ text: domain, italics: true, color: '666666', size: 22 })]
    })
  ]

  if (frameworkTags.length > 0) {
    headerChildren.push(new Paragraph({
      spacing: { after: 400 },
      children: [new TextRun({ text: `Frameworks: ${frameworkTags.join(', ')}`, color: '888888', size: 18 })]
    }))
  }

  const doc = new Document({
    styles: {
      default: {
        document: {
          run: { font: 'Calibri', size: 22 }
        }
      },
      paragraphStyles: [
        {
          id: 'Heading1', name: 'Heading 1', basedOn: 'Normal', next: 'Normal', quickFormat: true,
          run: { size: 32, bold: true, font: 'Calibri', color: '1F3864' },
          paragraph: { spacing: { before: 360, after: 200 } }
        },
        {
          id: 'Heading2', name: 'Heading 2', basedOn: 'Normal', next: 'Normal', quickFormat: true,
          run: { size: 28, bold: true, font: 'Calibri', color: '2E75B6' },
          paragraph: { spacing: { before: 240, after: 120 } }
        },
        {
          id: 'Heading3', name: 'Heading 3', basedOn: 'Normal', next: 'Normal', quickFormat: true,
          run: { size: 24, bold: true, font: 'Calibri', color: '404040' },
          paragraph: { spacing: { before: 200, after: 100 } }
        },
      ]
    },
    numbering: {
      config: [
        {
          reference: 'bullets',
          levels: [{
            level: 0, format: LevelFormat.BULLET, text: '\u2022', alignment: AlignmentType.LEFT,
            style: { paragraph: { indent: { left: 720, hanging: 360 } } }
          }]
        },
        {
          reference: 'numbers',
          levels: [{
            level: 0, format: LevelFormat.DECIMAL, text: '%1.', alignment: AlignmentType.LEFT,
            style: { paragraph: { indent: { left: 720, hanging: 360 } } }
          }]
        }
      ]
    },
    sections: [{
      properties: {
        page: {
          size: { width: 12240, height: 15840 },
          margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 }
        }
      },
      children: [...headerChildren, ...contentParagraphs]
    }]
  })

  const buffer = await Packer.toBlob(doc)
  saveAs(buffer, `${title.replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_')}.docx`)
}

// ============================================================
// Structured Content → DOCX Export (Tier 1/2/3 policies)
// ============================================================

export async function exportStructuredPolicyToDocx({ title, domain, structuredContent, frameworkTags = [], clientName }) {
  if (!structuredContent) throw new Error('No structured content to export')

  const sc = structuredContent
  const children = []

  // Title page header
  children.push(new Paragraph({
    spacing: { before: 600, after: 100 },
    alignment: AlignmentType.CENTER,
    children: [new TextRun({ text: 'SIDEWALK SECURITY ADVISORS', bold: true, size: 20, color: '888888', font: 'Calibri' })]
  }))
  children.push(new Paragraph({
    heading: HeadingLevel.HEADING_1,
    alignment: AlignmentType.CENTER,
    spacing: { after: 100 },
    children: [new TextRun({ text: title || domain || 'Security Policy', bold: true })]
  }))
  if (clientName) {
    children.push(new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 100 },
      children: [new TextRun({ text: `Prepared for: ${clientName}`, italics: true, color: '555555', size: 24 })]
    }))
  }
  if (frameworkTags.length > 0) {
    children.push(new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 },
      children: [new TextRun({ text: `Frameworks: ${frameworkTags.join(', ')}`, color: '888888', size: 18 })]
    }))
  }

  // Document Control
  if (sc.document_control) {
    const dc = sc.document_control
    children.push(new Paragraph({ heading: HeadingLevel.HEADING_2, children: [new TextRun({ text: 'Document Control', bold: true })] }))

    const dcRows = [
      ['Field', 'Value'],
      ['Policy ID', dc.policy_id || '[POLICY-ID]'],
      ['Owner', dc.owner || '[Policy Owner]'],
      ['Review Cycle', dc.review_cycle || 'Annual']
    ]
    const dcTable = buildTable(dcRows.map(r => `| ${r[0]} | ${r[1]} |`))
    if (dcTable) children.push(dcTable)
    children.push(new Paragraph({ spacing: { after: 200 }, children: [] }))
  }

  // Purpose
  if (sc.purpose) {
    children.push(new Paragraph({ heading: HeadingLevel.HEADING_2, children: [new TextRun({ text: 'Purpose', bold: true })] }))
    children.push(new Paragraph({ spacing: { after: 200 }, children: parseInlineFormatting(sc.purpose) }))
  }

  // Scope
  if (sc.scope) {
    children.push(new Paragraph({ heading: HeadingLevel.HEADING_2, children: [new TextRun({ text: 'Scope', bold: true })] }))
    if (sc.scope.applies_to) {
      children.push(new Paragraph({ spacing: { after: 100 }, children: [new TextRun({ text: 'Applies to: ', bold: true }), new TextRun({ text: sc.scope.applies_to })] }))
    }
    if (sc.scope.exclusions) {
      children.push(new Paragraph({ spacing: { after: 200 }, children: [new TextRun({ text: 'Exclusions: ', bold: true }), new TextRun({ text: sc.scope.exclusions })] }))
    }
  }

  // Definitions
  if (sc.definitions && sc.definitions.length > 0) {
    children.push(new Paragraph({ heading: HeadingLevel.HEADING_2, children: [new TextRun({ text: 'Definitions', bold: true })] }))
    for (const def of sc.definitions) {
      children.push(new Paragraph({
        spacing: { after: 100 },
        children: [
          new TextRun({ text: `${def.term}: `, bold: true }),
          new TextRun({ text: def.definition })
        ]
      }))
    }
    children.push(new Paragraph({ spacing: { after: 200 }, children: [] }))
  }

  // Policy Statements
  if (sc.policy_statements && sc.policy_statements.length > 0) {
    children.push(new Paragraph({ heading: HeadingLevel.HEADING_2, children: [new TextRun({ text: 'Policy Statements', bold: true })] }))
    for (const ps of sc.policy_statements) {
      children.push(new Paragraph({
        heading: HeadingLevel.HEADING_3,
        children: [new TextRun({ text: `${ps.section_id}: ${ps.title}`, bold: true })]
      }))
      if (ps.content) {
        children.push(new Paragraph({ spacing: { after: 100 }, children: parseInlineFormatting(ps.content) }))
      }
      if (ps.bullets && ps.bullets.length > 0) {
        for (const bullet of ps.bullets) {
          children.push(new Paragraph({
            numbering: { reference: 'bullets', level: 0 },
            children: parseInlineFormatting(bullet)
          }))
        }
      }
      children.push(new Paragraph({ spacing: { after: 200 }, children: [] }))
    }
  }

  // Roles and Responsibilities
  if (sc.roles_and_responsibilities && sc.roles_and_responsibilities.length > 0) {
    children.push(new Paragraph({ heading: HeadingLevel.HEADING_2, children: [new TextRun({ text: 'Roles and Responsibilities', bold: true })] }))
    for (const role of sc.roles_and_responsibilities) {
      children.push(new Paragraph({
        spacing: { after: 100 },
        children: [
          new TextRun({ text: `${role.role}: `, bold: true }),
          new TextRun({ text: role.responsibilities })
        ]
      }))
    }
    children.push(new Paragraph({ spacing: { after: 200 }, children: [] }))
  }

  // Compliance and Enforcement
  if (sc.compliance_and_enforcement) {
    children.push(new Paragraph({ heading: HeadingLevel.HEADING_2, children: [new TextRun({ text: 'Compliance and Enforcement', bold: true })] }))
    children.push(new Paragraph({ spacing: { after: 200 }, children: parseInlineFormatting(sc.compliance_and_enforcement) }))
  }

  // Related Documents
  if (sc.related_documents && sc.related_documents.length > 0) {
    children.push(new Paragraph({ heading: HeadingLevel.HEADING_2, children: [new TextRun({ text: 'Related Documents', bold: true })] }))
    for (const doc of sc.related_documents) {
      children.push(new Paragraph({
        numbering: { reference: 'bullets', level: 0 },
        children: [new TextRun({ text: doc })]
      }))
    }
  }

  // Footer
  children.push(new Paragraph({ spacing: { before: 600 }, children: [] }))
  children.push(new Paragraph({
    alignment: AlignmentType.CENTER,
    children: [new TextRun({ text: 'Generated by Sidewalk Security Advisors', italics: true, color: 'AAAAAA', size: 16 })]
  }))

  const docObj = new Document({
    styles: {
      default: {
        document: { run: { font: 'Calibri', size: 22 } }
      },
      paragraphStyles: [
        {
          id: 'Heading1', name: 'Heading 1', basedOn: 'Normal', next: 'Normal', quickFormat: true,
          run: { size: 32, bold: true, font: 'Calibri', color: '1F3864' },
          paragraph: { spacing: { before: 360, after: 200 } }
        },
        {
          id: 'Heading2', name: 'Heading 2', basedOn: 'Normal', next: 'Normal', quickFormat: true,
          run: { size: 28, bold: true, font: 'Calibri', color: '2E75B6' },
          paragraph: { spacing: { before: 240, after: 120 } }
        },
        {
          id: 'Heading3', name: 'Heading 3', basedOn: 'Normal', next: 'Normal', quickFormat: true,
          run: { size: 24, bold: true, font: 'Calibri', color: '404040' },
          paragraph: { spacing: { before: 200, after: 100 } }
        },
      ]
    },
    numbering: {
      config: [
        {
          reference: 'bullets',
          levels: [{
            level: 0, format: LevelFormat.BULLET, text: '\u2022', alignment: AlignmentType.LEFT,
            style: { paragraph: { indent: { left: 720, hanging: 360 } } }
          }]
        },
        {
          reference: 'numbers',
          levels: [{
            level: 0, format: LevelFormat.DECIMAL, text: '%1.', alignment: AlignmentType.LEFT,
            style: { paragraph: { indent: { left: 720, hanging: 360 } } }
          }]
        }
      ]
    },
    sections: [{
      properties: {
        page: {
          size: { width: 12240, height: 15840 },
          margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 }
        }
      },
      children
    }]
  })

  const buffer = await Packer.toBlob(docObj)
  const filename = (title || domain || 'Policy').replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_')
  saveAs(buffer, `${filename}.docx`)
}

// ============================================================
// PDF Export — captures rendered PolicyPreview via html2canvas
// ============================================================

export async function exportPolicyToPdf({ title, elementId }) {
  const element = document.getElementById(elementId)
  if (!element) throw new Error('Preview element not found')

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff'
  })

  const imgData = canvas.toDataURL('image/png')
  const pdf = new jsPDF('p', 'mm', 'letter')
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 10
  const imgWidth = pageWidth - margin * 2
  const imgHeight = (canvas.height * imgWidth) / canvas.width

  let heightLeft = imgHeight
  let position = margin

  pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight)
  heightLeft -= (pageHeight - margin * 2)

  while (heightLeft > 0) {
    position = heightLeft - imgHeight + margin
    pdf.addPage()
    pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight)
    heightLeft -= (pageHeight - margin * 2)
  }

  pdf.save(`${title.replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_')}.pdf`)
}
